import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, Search, ArrowLeft, Shield, Flag, Loader2, MessageCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/app/Header";
import Avatar from "../components/app/Avatar";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProChatLock, canSendMessages } from "../components/app/ProChatLock";

/**
 * Messages — member-to-member DMs (iter142).
 *
 * Layout: two-pane desktop, single-pane mobile with active-thread take-over.
 * Real-time-ish via a 3s poll of /dms/conversations while the tab is
 * visible. Cheap, robust behind Cloudflare, easy SSE upgrade later.
 */
export default function Messages() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialConv = searchParams.get("c") || null;
  const [conversations, setConversations] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [activeConv, setActiveConv] = useState(initialConv);
  const [dirOpen, setDirOpen] = useState(false);

  const refreshConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/dms/conversations");
      setConversations(data.items || []);
      setTotalUnread(data.total_unread || 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshConvs();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refreshConvs();
    }, 3000);
    return () => clearInterval(id);
  }, [user, refreshConvs]);

  if (!user) {
    return (
      <>
        <Header />
        <main className="max-w-md mx-auto px-4 py-16 text-center">
          <MessageCircle className="w-10 h-10 mx-auto text-[#ff4500] mb-3" />
          <h1 className="font-display text-3xl mb-2">Sign in to message campers</h1>
          <p className="text-sm text-[#666] mb-4">Real-time messaging is a member-only feature.</p>
          <Link to="/login" className="btn-primary inline-block">Log in</Link>
        </main>
      </>
    );
  }

  const openConversation = (cid) => {
    setActiveConv(cid);
    setSearchParams({ c: cid });
  };
  const closeConversation = () => {
    setActiveConv(null);
    setSearchParams({});
    refreshConvs();
  };

  return (
    <>
      <Header />
      <main className="max-w-[1200px] mx-auto px-3 sm:px-6 py-6" data-testid="messages-page">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl">Messages</h1>
            <p className="text-xs text-[#666] mt-0.5">
              {totalUnread > 0 ? `${totalUnread} unread` : "You're all caught up"}
            </p>
          </div>
          {canSendMessages(user) ? (
            <button
              onClick={() => setDirOpen(true)}
              className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-1.5"
              data-testid="messages-new-btn"
            >
              <Search className="w-3.5 h-3.5" /> Find a camper
            </button>
          ) : (
            <Link
              to="/pro"
              className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-1.5"
              data-testid="messages-new-btn-locked"
            >
              <Lock className="w-3.5 h-3.5" /> Find a camper · Pro
            </Link>
          )}
        </div>

        {/* iter150 — Prominent entry-point to the community-wide open chat.
            Without it campers only ever found /messages (1:1 DMs) and never
            realised there was a shared room. */}
        <Link
          to="/chatroom"
          className="mb-4 flex items-center justify-between gap-3 p-4 rounded-2xl border hairline bg-gradient-to-r from-[#fff4ee] to-white hover:from-[#ffe6d5] transition-colors"
          data-testid="messages-chatroom-cta"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-11 h-11 rounded-full bg-[#ff4500] text-white flex items-center justify-center text-lg" aria-hidden="true">
              🔥
            </div>
            <div className="min-w-0">
              <div className="font-black text-sm sm:text-base tracking-tight">Campfire chat room</div>
              <div className="text-xs text-[#666] mt-0.5 truncate">
                One shared room — ask the whole community anything, or just say hi
              </div>
            </div>
          </div>
          <span className="shrink-0 text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-[#ff4500]">
            Enter →
          </span>
        </Link>

        <div className={`grid ${activeConv ? "md:grid-cols-[320px_1fr]" : "md:grid-cols-[320px_1fr]"} gap-3 border hairline bg-white min-h-[60vh]`}>
          {/* Conversation list — hidden on mobile when a thread is open */}
          <aside className={`${activeConv ? "hidden md:block" : ""} border-r hairline`} data-testid="messages-list">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-xs text-[#888]">
                No messages yet. Tap <em>Find a camper</em> above to start a conversation.
              </div>
            ) : (
              <ul>
                {conversations.map((c) => (
                  <li key={c.conversation_id}>
                    <button
                      onClick={() => openConversation(c.conversation_id)}
                      className={`w-full text-left px-4 py-3 border-b hairline hover:bg-[#fafafa] transition ${activeConv === c.conversation_id ? "bg-[#fff5ee]" : ""}`}
                      data-testid={`messages-conv-${c.conversation_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar src={c.other_user.picture} name={c.other_user.display_name} size={40} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-sm truncate">{c.other_user.display_name}</span>
                            {c.unread > 0 && (
                              <span className="bg-[#ff4500] text-white text-[10px] font-bold px-1.5 rounded-full min-w-[18px] text-center">
                                {c.unread}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[#666] truncate">
                            {c.last_message?.text || <em>New conversation</em>}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Active thread panel */}
          <section className={`${activeConv ? "" : "hidden md:flex md:items-center md:justify-center"} min-h-[60vh]`}>
            {activeConv ? (
              <ThreadPanel conversationId={activeConv} onClose={closeConversation} onSent={refreshConvs} />
            ) : (
              <div className="text-sm text-[#888] p-6 text-center">
                Pick a conversation from the left, or start a new one.
              </div>
            )}
          </section>
        </div>
      </main>

      {dirOpen && <DirectoryModal onClose={() => setDirOpen(false)} onPick={(cid) => { setDirOpen(false); openConversation(cid); refreshConvs(); }} />}
    </>
  );
}

function ThreadPanel({ conversationId, onClose, onSent }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [otherName, setOtherName] = useState("");
  const [otherPicture, setOtherPicture] = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/dms/conversations/${conversationId}/messages?limit=100`);
      setMsgs(data.items || []);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 30);
    } catch (e) { /* silent */ }
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 3000);
    return () => clearInterval(id);
  }, [load]);

  // Look up other participant from the sibling conversation list via a lookup call
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dms/conversations");
        const c = (data.items || []).find((x) => x.conversation_id === conversationId);
        if (c) {
          setOtherName(c.other_user?.display_name || "Camper");
          setOtherPicture(c.other_user?.picture || null);
        }
      } catch { /* noop */ }
    })();
  }, [conversationId]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/dms/conversations/${conversationId}/send`, { text: t });
      setText("");
      await load();
      onSent && onSent();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col w-full h-[60vh] md:h-[70vh]" data-testid="messages-thread">
      <header className="flex items-center gap-2 px-4 py-3 border-b hairline">
        <button onClick={onClose} className="md:hidden p-1 hover:bg-[#fafafa]" data-testid="messages-thread-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <Avatar src={otherPicture} name={otherName} size={32} />
        <span className="font-semibold text-sm">{otherName || "Camper"}</span>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-[#fafafa]">
        {msgs.length === 0 ? (
          <div className="text-center text-xs text-[#888] mt-8">Say hi 👋</div>
        ) : (
          msgs.map((m) => {
            const mine = m.sender_id === user.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"} items-end gap-1.5`}>
                {!mine && <Avatar src={otherPicture} name={otherName} size={22} ringed={false} />}
                <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#ff4500] text-white rounded-br-sm" : "bg-white border hairline rounded-bl-sm"}`}>
                  {m.text}
                  <div className={`text-[9px] mt-1 opacity-70 ${mine ? "text-white/80" : "text-[#666]"}`}>
                    {new Date(m.sent_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!canSendMessages(user) ? (
        <ProChatLock what="reply and start conversations" testId="messages-pro-lock" />
      ) : (
      <form onSubmit={send} className="flex gap-2 p-3 border-t hairline bg-white">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 2000))}
          placeholder="Type a message…"
          className="flex-1 border hairline px-3 py-2 text-sm focus:outline-none focus:border-[#ff4500]"
          data-testid="messages-send-input"
          maxLength={2000}
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="messages-send-btn"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
        </button>
      </form>
      )}
    </div>
  );
}

function DirectoryModal({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyPick, setBusyPick] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/dms/directory", { params: { q: q || undefined, limit: 40 } });
        if (alive) setItems(data.items || []);
      } catch { /* noop */ }
      finally { if (alive) setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const pick = async (uid) => {
    setBusyPick(uid);
    try {
      const { data } = await api.post("/dms/start", { recipient_id: uid });
      onPick(data.conversation.conversation_id);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusyPick(null); }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="messages-directory">
      <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-md w-full p-6 border-2 border-[#ff4500] shadow-[0_8px_0_0_#111] max-h-[80vh] flex flex-col">
        <h2 className="font-display text-2xl mb-1">Find a camper</h2>
        <p className="text-xs text-[#666] mb-3">Search everyone on CampCompass. Tap to start a conversation.</p>
        <input
          autoFocus
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name…"
          className="w-full border hairline px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#ff4500]"
          data-testid="messages-directory-search"
        />
        <div className="flex-1 overflow-y-auto -mx-2 px-2">
          {loading ? (
            <div className="text-xs text-[#888] py-4">Searching…</div>
          ) : items.length === 0 ? (
            <div className="text-xs text-[#888] py-4">No campers match.</div>
          ) : (
            <ul>
              {items.map((u) => (
                <li key={u.user_id}>
                  <button
                    onClick={() => pick(u.user_id)}
                    disabled={busyPick === u.user_id}
                    className="w-full text-left px-3 py-2 border-b hairline hover:bg-[#fafafa] flex items-center gap-3 disabled:opacity-50"
                    data-testid={`messages-directory-item-${u.user_id}`}
                  >
                    <Avatar src={u.picture} name={u.display_name} size={32} />
                    <span className="text-sm font-semibold">{u.display_name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button onClick={onClose} className="text-xs mt-3 text-[#666] hover:text-[#ff4500]">Close</button>
      </div>
    </div>
  );
}
